import React from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { heroStats, brand, certifications } from "../mock";

const heroCertificationCodes = ["DP-700", "PL-300", "DEA", "SnowPro"];
const heroCertifications = certifications.filter((cert) => heroCertificationCodes.includes(cert.code));

const Hero = () => {
  return (
    <section className="relative overflow-hidden bg-[#0B1B3B] text-white pt-[120px] pb-24">
      {/* grid backdrop */}
      <div className="absolute inset-0 grid-lines-bg opacity-60" />

      {/* gradient orbs */}
      <div className="absolute -top-20 -right-20 w-[420px] h-[420px] rounded-full orb"
           style={{ background: "radial-gradient(closest-side, rgba(30,86,227,0.35), transparent 70%)" }} />
      <div className="absolute bottom-0 left-1/3 w-[380px] h-[380px] rounded-full orb"
           style={{ background: "radial-gradient(closest-side, rgba(80,140,255,0.20), transparent 70%)" }} />

      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 rounded-full text-xs font-medium tracking-wider uppercase text-[#9FB6FF]">
              <Sparkles size={14} /> Enterprise Data Engineering · Cloud · AI
            </div>
            <h1 className="mt-6 text-4xl md:text-6xl font-extrabold leading-[1.05] tracking-tight">
              Enterprise Data Engineering Services —{" "}
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(90deg,#7CA6FF,#3D82FF)" }}>
                Databricks, Snowflake, Azure & AWS
              </span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
              From data pipelines and cloud lakehouses on Databricks and Snowflake, to real-time streaming on Azure and Microsoft Fabric — V4 Data And AI Solutions builds the data infrastructure modern enterprises depend on.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="/services" className="btn-primary" data-testid="hero-services-button">
                Explore Services
              </a>
              <Link
                to={brand.bookingPath}
                className="btn-outline"
                data-testid="hero-book-appointment-button"
              >
                Book Appointment
              </Link>
            </div>
          </div>

          {/* visual card */}
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-8 backdrop-blur">
              <div className="text-[11px] uppercase tracking-widest text-[#7CA6FF] font-bold">Certified Engineering</div>
              <div className="mt-3 text-2xl font-extrabold text-white leading-tight">Built by a certified data engineer</div>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                Every architectural decision backed by hands-on certifications across the platforms Fortune-500 data teams run on.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {heroCertifications.map((c) => (
                  <div key={c.code} className="rounded-lg bg-[#0F2452] border border-white/10 p-3 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-white/95 p-1.5 shrink-0 flex items-center justify-center overflow-hidden">
                      {c.badgeImage ? (
                        <img src={c.badgeImage} alt={c.title} className="h-full w-full object-contain" />
                      ) : (
                        <span className="h-8 w-8 rounded-md flex items-center justify-center text-[9px] font-extrabold text-white" style={{ background: c.color }}>{c.icon}</span>
                      )}
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-400 uppercase tracking-wider">{c.code}</div>
                      <div className="text-xs font-semibold text-white leading-tight">{c.title}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center justify-between text-[11px] text-slate-400">
                <span>9+ years enterprise data engineering</span>
                <span className="text-[#7CA6FF] font-semibold">10+ PB governed</span>
              </div>
            </div>
          </div>
        </div>

        {/* stats bar */}
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {heroStats.map((s) => (
            <div key={s.label} className="reveal border-t border-white/10 pt-6">
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-extrabold text-white">{s.value}</span>
                <span className="text-lg font-semibold text-[#7CA6FF]">{s.label}</span>
              </div>
              <p className="mt-2 text-sm text-slate-400 max-w-xs">{s.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
